import { useEffect, useRef, useState, useCallback } from "react";
import MSEPlayerManager from "./MSEPlayerManager";
import type { PlayerStatus, PlayerHookReturn } from "../types";
import { diagnosticEventLog } from "../../../shared/services/diagnostic";

export const MAX_MSE_RETRIES = 10;
export const RETRY_DELAY_MS = 1500;

interface UseMSEPlayerOptions {
  audioElement: HTMLVideoElement | null;
  address: string | null;
  enabled: boolean; // Only connect when enabled
  authenticated: boolean;
}

/**
 * React hook for managing MSE audio player
 *
 * Handles connection, retries, and cleanup automatically
 */
export const useMSEPlayer = ({
  audioElement,
  address,
  enabled,
  authenticated,
}: UseMSEPlayerOptions): PlayerHookReturn => {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const managerRef = useRef<MSEPlayerManager | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const currentAddressRef = useRef<string | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const skipRetryIncrementRef = useRef<boolean>(false);

  // Initialize manager on mount
  useEffect(() => {
    if (!managerRef.current) {
      managerRef.current = new MSEPlayerManager();
    }
  }, []);

  // Set volume helper
  const setVolume = useCallback((volume: number) => {
    if (managerRef.current) {
      managerRef.current.setVolume(volume);
    }
  }, []);

  // Connection effect
  useEffect(() => {
    // Don't connect if not enabled, not authenticated, or missing dependencies
    if (!enabled || !authenticated || !address || !audioElement) {
      // Disconnect if we have an active connection
      if (managerRef.current?.isActive()) {
        managerRef.current?.disconnect();
      }

      // Reset state
      setStatus("idle");
      setError(null);
      setRetryCount(0);
      currentAddressRef.current = null;
      isConnectingRef.current = false;

      // Clear any pending retries
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }

    // If already connecting to the same address, don't restart
    // This prevents duplicate connections during rapid re-renders or retries
    if (
      isConnectingRef.current &&
      currentAddressRef.current === address
    ) {
      console.log(`[useMSEPlayer] Already connecting to ${address}, skipping duplicate connection attempt`);
      return;
    }

    // If already connected with the same address, don't restart
    if (
      managerRef.current?.isActive() &&
      managerRef.current.address === address
    ) {
      console.log(`[useMSEPlayer] Already active connection to ${address}, skipping reconnection`);
      return;
    }

    // Reset state for new connection
    setStatus("idle");
    setError(null);
    setRetryCount(0);

    // Clear any pending retries
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // Mark that we're starting a connection to this address
    currentAddressRef.current = address;
    isConnectingRef.current = true;

    // Note: We don't check MSEPlayerManager.isSupported() here because:
    // 1. The check can be unreliable during initial setup
    // 2. If MSE is not supported, the player will fail gracefully with a proper error
    // 3. This prevents false negatives that trigger unnecessary retries

    console.log(`[useMSEPlayer] Starting connection to ${address}`);
    diagnosticEventLog.record("mse:audio", "info", "connection-start", { connectionId: managerRef.current?.connectionId ?? 0, address });
    setStatus("connecting");

    const onSuccess = () => {
      console.log(`[useMSEPlayer] Connection successful to ${address}`);
      diagnosticEventLog.record("mse:audio", "info", "connected", { connectionId: managerRef.current?.connectionId ?? 0, address });
      isConnectingRef.current = false;
      setStatus("connected");
      setError(null);
      setRetryCount(0);
    };

    const onError = (err: Error) => {
      console.log(`[useMSEPlayer] Connection error to ${address}:`, err);
      diagnosticEventLog.record("mse:audio", "error", "connection-error", { connectionId: managerRef.current?.connectionId ?? 0, address, error: err.message });
      isConnectingRef.current = false;
      // Codec mismatch / decode errors are recoverable — don't count against retry limit
      if ((err as Error & { _skipRetryCount?: boolean })._skipRetryCount) {
        skipRetryIncrementRef.current = true;
      }
      setStatus("error");
      setError(err);
    };

    // Start connection
    managerRef.current
      ?.connect({
        audioElement,
        address,
        onSuccess,
        onError,
      })
      .catch((err: Error) => {
        console.error(`[useMSEPlayer] Unexpected error in MSE connect to ${address}:`, err);
        isConnectingRef.current = false;
        setStatus("error");
        setError(err);
      });

    // Cleanup on unmount or dependency change
    return () => {
      console.log(`[useMSEPlayer] Cleanup triggered for ${address}`);
      setStatus("disconnected");
      isConnectingRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      managerRef.current?.disconnect();
    };
  }, [enabled, authenticated, address, audioElement]);

  // Retry logic - separate effect to avoid re-triggering connection
  useEffect(() => {
    if (status !== "error" || !error || !enabled || !address || !audioElement) {
      return;
    }

    // Don't retry if we've exceeded max retries
    if (retryCount >= MAX_MSE_RETRIES) {
      console.error("[useMSEPlayer] Max MSE retries reached, giving up");
      diagnosticEventLog.record("mse:audio", "error", "max-retries-reached", { retryCount });
      isConnectingRef.current = false;
      return;
    }

    // Don't retry if already connecting (prevents duplicate retry attempts)
    if (isConnectingRef.current) {
      console.log(`[useMSEPlayer] Already connecting, skipping retry attempt`);
      return;
    }

    // Recoverable errors (codec mismatch, decode failure) use a shorter delay
    // and don't increment the retry counter.
    const isRecoverable = skipRetryIncrementRef.current;
    const delayMs = isRecoverable ? 500 : RETRY_DELAY_MS;

    console.log(
      `[useMSEPlayer] Retrying MSE connection in ${delayMs / 1000}s... (attempt ${retryCount + 1}/${MAX_MSE_RETRIES}${isRecoverable ? ", recoverable — no retry penalty" : ""})`
    );
    diagnosticEventLog.record("mse:audio", "info", "retry-scheduled", { retryCount: retryCount + 1, maxRetries: MAX_MSE_RETRIES, delayMs, recoverable: isRecoverable });

    retryTimeoutRef.current = setTimeout(() => {
      if (!enabled) return;

      // Double-check we're not already connecting before retry
      if (isConnectingRef.current) {
        console.log(`[useMSEPlayer] Already connecting during retry timeout, aborting retry`);
        return;
      }

      console.log(`[useMSEPlayer] Executing retry attempt ${retryCount + 1}/${MAX_MSE_RETRIES}`);

      isConnectingRef.current = true;
      // Skip retry count increment for recoverable errors (server-side codec/decode resets)
      if (skipRetryIncrementRef.current) {
        skipRetryIncrementRef.current = false;
      } else {
        setRetryCount((prev) => prev + 1);
      }
      setStatus("connecting");
      setError(null);

      const onSuccess = () => {
        console.log(`[useMSEPlayer] Retry successful to ${address}`);
        isConnectingRef.current = false;
        setStatus("connected");
        setError(null);
      };

      const onError = (err: Error) => {
        console.log(`[useMSEPlayer] Retry error to ${address}:`, err);
        isConnectingRef.current = false;
        if ((err as Error & { _skipRetryCount?: boolean })._skipRetryCount) {
          skipRetryIncrementRef.current = true;
        }
        setStatus("error");
        setError(err);
      };

      managerRef.current
        ?.connect({
          audioElement,
          address,
          onSuccess,
          onError,
        })
        .catch((err: Error) => {
          console.error(`[useMSEPlayer] Unexpected error in MSE retry to ${address}:`, err);
          isConnectingRef.current = false;
          setStatus("error");
          setError(err);
        });
    }, delayMs);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [status, error, retryCount, enabled, address, audioElement]);

  // Visibility handler — silently reconnect MSE when tab becomes visible.
  // The MSE player uses a <video> element which Chrome can pause in background tabs.
  useEffect(() => {
    if (!enabled || !authenticated || !address || !audioElement) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      const manager = managerRef.current;
      if (!manager) return;

      const state = manager.getState();
      // Already connected or connecting — nothing to do
      if (state === "connected" || state === "connecting") return;
      // Already in a retry loop — let it handle reconnection
      if (isConnectingRef.current) return;

      console.log("[useMSEPlayer] Tab visible — silently reconnecting MSE player");
      diagnosticEventLog.record("mse:audio", "info", "visibility-reconnect", {
        previousState: state,
      });

      // Reset stall cooldown so the first stall after tab return isn't blocked
      manager.resetStallCooldown?.();

      // Clean reconnect: reset retry count and connect directly
      isConnectingRef.current = true;
      setRetryCount(0);
      setStatus("connecting");
      setError(null);

      manager
        .connect({
          audioElement,
          address,
          onSuccess: () => {
            isConnectingRef.current = false;
            setStatus("connected");
            setError(null);
          },
          onError: (err: Error) => {
            isConnectingRef.current = false;
            setStatus("error");
            setError(err);
          },
        })
        .catch((err: Error) => {
          isConnectingRef.current = false;
          setStatus("error");
          setError(err);
        });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, authenticated, address, audioElement]);

  return {
    status,
    error,
    retryCount,
    setVolume,
  };
};
